import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Input as AntdInput } from 'antd'
import strings from 'utils/strings.js'
import { classNames, isNumeric, isEmptyString } from 'utils/utils'
import { CHEC_MAX_VALUE } from 'utils/constants.js'
import { Label } from 'components/chec'
import { toLower, isNil } from 'ramda'
import { isNumber } from 'utils/types'

export default class InputNumber extends Component {
  constructor(props) {
    super(props)
    this.state = {
      isValid: !props.customErrorMessage,
    }
  }

  componentDidMount = () => {
    if (!this.props.noInitialValidation) {
      // second argument must be a valid number initially
      this.validateInput(this.props.value, this.props.min)
    }
  }

  componentDidUpdate = (prevProps) => {
    const { value, customErrorMessage } = this.props
    if (
      prevProps.value !== value ||
      prevProps.customErrorMessage ^ customErrorMessage
    ) {
      // At the moment validation is the same for all input fields.
      // When this changes, the condition above needs to change so the
      // field can be re-validated using the updated value.
      return this.validateInput(
        value,
        prevProps.value,
        prevProps.customErrorMessage
      )
    }
  }

  isInRange = (val) => {
    if (isNil(val)) return false
    const { min, max } = this.props
    const aboveMax = isNumber(max) && val > max
    const belowMin = isNumber(min) && val < min

    const rangeError = !isNumber(val) || aboveMax || belowMin
    return !rangeError
  }

  validateInput = (val, prevValue, prevErrorMsg) => {
    const {
      min,
      max,
      name,
      onValidityChange,
      paramName,
      customErrorMessage,
    } = this.props

    const rangeError = !this.isInRange(val)
    const isValid = !rangeError && !customErrorMessage

    if (isValid ^ this.isInRange(prevValue)) {
      const errorMsg =
        customErrorMessage ||
        prevErrorMsg ||
        strings.numeric_range_error(name, min, max)
      this.setState({ isValid })
      onValidityChange(isValid, paramName, errorMsg)
    }
  }

  onChange = ({ target: { value } }) => {
    if (
      (isEmptyString(value) || isNumeric(value)) && // allow the user to clear the field, ensure their entry is a number
      value !== this.props.value
    ) {
      const returnValue =
        isNil(value) || value === '' ? null : parseInt(value, 10)
      this.props.onChange(returnValue)
    }
  }

  render() {
    const {
      value,
      placeholder,
      defaultValue,
      name,
      disabled,
      size,
      min,
      max,
      className,
      label,
      hint,
      vertical,
      addonAfter,
      customErrorMessage,
    } = this.props
    const { isValid } = this.state

    const classes = classNames('chec-input-number', className, {
      'has-error': !isValid,
    })

    return (
      <div
        className={classNames('form-field', 'input-number-container', {
          vertical,
          [`${label && toLower(label)}-input-number`]: label,
        })}
      >
        {label && <Label text={label} hint={hint} />}

        <span>
          <AntdInput
            addonAfter={addonAfter}
            className={classes}
            value={value}
            placeholder={placeholder}
            defaultValue={defaultValue}
            disabled={disabled}
            onChange={this.onChange}
            size={size}
          />
          {!isValid && (
            <span className="error-msg">
              {customErrorMessage ||
                strings.numeric_range_error(name, min, max)}
            </span>
          )}
        </span>
      </div>
    )
  }
}

InputNumber.defaultProps = {
  className: '',
  name: '',
  min: 0,
  max: CHEC_MAX_VALUE,
  paramName: 'inputNumber',
  onValidityChange: () => {},
  value: 0,
  vertical: false,
  noInitialValidation: false,
  hint: null,
  label: null,
}

InputNumber.propTypes = {
  noInitialValidation: PropTypes.bool,
  className: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
  name: PropTypes.string, // the name that should be displayed in the error message.
  onChange: PropTypes.func.isRequired,
  onValidityChange: PropTypes.func,
  paramName: PropTypes.string, // optional. Used only if custom error handling is desired. see SET_CURR_SP_FIELD_VALIDITY reducer
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  size: PropTypes.string,
  label: PropTypes.string,
  hint: PropTypes.string,
  vertical: PropTypes.bool,
  addonAfter: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  customErrorMessage: PropTypes.string,
}
